import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  AlertCircle,
  AlertTriangle,
  Building2,
  Clock,
  FileWarning,
  KeyRound,
  Lock,
  RefreshCw,
  ServerCrash,
  ShieldAlert,
  WifiOff,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { isFetchBaseQueryError } from '@/services/baseApi';
import { Button } from './button';
import { StatePanel } from './state-panel';

/**
 * `error` arrives as `unknown` on purpose: RTK Query produces three different
 * shapes and none of them is a real `Error`.
 *
 * - `FetchBaseQueryError` with `status: number` — the API's normal case, the
 *   HTTP status the backend answered with.
 * - `FetchBaseQueryError` with `status: 'FETCH_ERROR' | 'PARSING_ERROR' |
 *   'TIMEOUT_ERROR' | 'CUSTOM_ERROR'` — with no numeric status. There was no
 *   HTTP response to map, so these are resolved separately.
 * - Redux's `SerializedError` (or a plain `Error`) — it carries no `status`.
 *
 * `isFetchBaseQueryError` (defined in `services/baseApi.ts`, where the
 * `fetchBaseQuery` producing these errors lives) is the guard separating the
 * first and second cases from the third.
 */

/** Mapping of status/reason → copy. Data, not a chain of `if`s: adding a case
 * is adding a row, and a test can walk all of them. */
interface ErrorPresentation {
  tone: 'warning' | 'danger';
  icon: LucideIcon;
  title: string;
  description: string;
  /** A 401 is not retried: the session expired and retrying gives another 401.
   * The same for a 403 — it is not a transient problem. */
  retryable: boolean;
}

const SESSION_EXPIRED_PRESENTATION: ErrorPresentation = {
  tone: 'warning',
  icon: Lock,
  title: 'Sesión expirada',
  description: 'Tu sesión terminó. Inicia sesión de nuevo para continuar.',
  retryable: false,
};

/**
 * The other 401: the credential that was sent is no good. It is not the session,
 * so saying «inicia sesión de nuevo» sends somebody to repeat something that
 * will change nothing.
 *
 * This distinction is not theoretical: **it is the bug that had to be fixed**.
 * While the SSO session did not yet authenticate the data plane, every call from
 * the SPA came back with `Missing API key` and the screen said «Sesión
 * expirada»; the person signed in again, the session was perfectly fine, and
 * they were left exactly where they started. The message described a cause that
 * was not it, and the action it offered could not work.
 */
const CREDENTIAL_PRESENTATION: ErrorPresentation = {
  tone: 'warning',
  icon: KeyRound,
  title: 'Credencial no válida',
  description:
    'La credencial con la que se llamó a la API no sirve. Si estás usando una clave de API, revísala; si entraste con tu cuenta, vuelve a iniciar sesión.',
  retryable: false,
};

/**
 * Which 401 it is, read from the **code** the backend sends in `code`
 * (`src/modules/auth/auth-error-codes.ts`): `invalid_api_key`,
 * `missing_credentials`, `missing_session`, `invalid_session`.
 *
 * It classifies by code and not by the message's text, and that distinction has
 * a scar: the first version of this fix ran `/api key/i` over the message, and
 * the MOST COMMON 401 — an expired cookie — answers «Missing credentials: send a
 * tenant API key, or sign in…», which contains "API key". That is, it sent
 * people to check a credential they do not use, which is exactly the bug this
 * fix came to correct. The PR's automated review caught it.
 *
 * A message is for reading; a code is for deciding. The backend's text is
 * **never shown**: it is in English and exposes detail that should not surface.
 *
 * With no code (a 401 from another layer, or a response with no body) the
 * session wins: in a SPA that authenticates with a cookie it is the likelier
 * one, and it is the only one of the two actions somebody can carry out
 * themselves.
 */
function unauthorizedPresentation(error: unknown): ErrorPresentation {
  return backendCode(error) === 'invalid_api_key'
    ? CREDENTIAL_PRESENTATION
    : SESSION_EXPIRED_PRESENTATION;
}

/**
 * The `requestId` the backend puts in a 500's body
 * (`common/api-error.filter.ts`), when it carries one.
 */
function backendRequestId(error: unknown): string | null {
  if (!isFetchBaseQueryError(error)) return null;
  const data: unknown = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return null;
  const id = (data as { requestId?: unknown }).requestId;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/** The `code` of a backend authentication error, when it carries one. */
function backendCode(error: unknown): string | null {
  if (!isFetchBaseQueryError(error)) return null;
  const data: unknown = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return null;
  const code = (data as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

/**
 * The 400 that is NOT the data's fault: the person belongs to more than one
 * organisation and the call left without saying which one it was for
 * (`X-Tenant-Id`).
 *
 * It has copy of its own because the generic 4xx text — «verifica los datos e
 * inténtalo de nuevo» — was literally false here, and that is how it arrived
 * from production: the onboarding wizard showed it under «Se creará la empresa
 * TCIT SpA…», and at the same time EVERY other view said the same thing. There
 * was nothing to check in any form; the active organisation was what needed
 * resolving. Sending people to check the data is the same class of error as the
 * «Sesión expirada» over a live session documented above: it describes a cause
 * that is not it and offers an action that unblocks nothing.
 */
const TENANT_SELECTION_PRESENTATION: ErrorPresentation = {
  tone: 'warning',
  icon: Building2,
  title: 'Elige una organización',
  description:
    'Tu cuenta pertenece a más de una organización y esta pantalla todavía no sabe en cuál estás trabajando. Elígela en el menú de arriba a la derecha; si ya lo hiciste, actualiza la página.',
  retryable: true,
};

/**
 * The chosen organisation no longer corresponds to a live membership (they were
 * removed from it, or one that no longer exists stayed stored). The generic 403
 * would say «sin permiso… contacta a un administrador», which sends people to
 * ask for something they do not need: the action is to choose another one.
 */
const TENANT_STALE_PRESENTATION: ErrorPresentation = {
  tone: 'warning',
  icon: Building2,
  title: 'Esa organización ya no está disponible',
  description:
    'La organización que estabas usando ya no corresponde a tu cuenta. Elige otra en el menú de arriba a la derecha.',
  retryable: false,
};

/**
 * The session is valid but leads to no organisation: the account has none
 * active. The generic 403 says «No tienes permiso para ver este contenido»,
 * which describes something else — here it is not a permission over something
 * that is missing, it is the whole organisation — and it is besides the only one
 * of the three tenant states the person can NOT resolve alone.
 */
const NO_ORGANISATION_PRESENTATION: ErrorPresentation = {
  tone: 'warning',
  icon: Building2,
  title: 'Tu cuenta no tiene ninguna organización activa',
  description:
    'No hay ninguna organización asociada a tu cuenta, así que no hay datos que mostrar. Si esto no es lo que esperabas, contacta a quien administra tu organización en factit.',
  retryable: false,
};

const HTTP_STATUS_PRESENTATIONS: Record<number, ErrorPresentation> = {
  401: SESSION_EXPIRED_PRESENTATION,
  403: {
    tone: 'warning',
    icon: ShieldAlert,
    title: 'Sin permiso',
    description:
      'No tienes permiso para ver este contenido. Si crees que es un error, contacta a un administrador.',
    retryable: false,
  },
  409: {
    tone: 'warning',
    icon: AlertTriangle,
    title: 'Conflicto con el estado actual',
    description:
      'La información cambió desde que se cargó esta vista. Actualiza la página e inténtalo de nuevo.',
    retryable: true,
  },
};

const SERVER_ERROR_PRESENTATION: ErrorPresentation = {
  tone: 'danger',
  icon: ServerCrash,
  title: 'Error del servidor',
  description: 'Ocurrió un problema en el servidor. Inténtalo de nuevo en unos minutos.',
  retryable: true,
};

const CLIENT_ERROR_PRESENTATION: ErrorPresentation = {
  tone: 'warning',
  icon: AlertCircle,
  title: 'No se pudo completar la solicitud',
  description: 'Verifica los datos e inténtalo de nuevo.',
  retryable: true,
};

/** The four `FetchBaseQueryError` reasons with no numeric HTTP status. */
const RTK_QUERY_REASON_PRESENTATIONS: Record<
  'FETCH_ERROR' | 'PARSING_ERROR' | 'TIMEOUT_ERROR' | 'CUSTOM_ERROR',
  ErrorPresentation
> = {
  // "There was no network" is not the same as a 500 for whoever is looking at
  // the screen, and it is not fixed with the same words.
  FETCH_ERROR: {
    tone: 'danger',
    icon: WifiOff,
    title: 'Sin conexión',
    description:
      'No se pudo conectar con el servidor. Revisa tu conexión a internet e inténtalo de nuevo.',
    retryable: true,
  },
  TIMEOUT_ERROR: {
    tone: 'warning',
    icon: Clock,
    title: 'Tiempo de espera agotado',
    description: 'El servidor tardó demasiado en responder. Inténtalo de nuevo.',
    retryable: true,
  },
  PARSING_ERROR: {
    tone: 'danger',
    icon: FileWarning,
    title: 'Respuesta inesperada del servidor',
    description: 'El servidor respondió en un formato que no se pudo interpretar.',
    retryable: true,
  },
  CUSTOM_ERROR: {
    tone: 'danger',
    icon: AlertCircle,
    title: 'Ocurrió un error inesperado',
    description: 'No se pudo procesar la solicitud.',
    retryable: true,
  },
};

/** Redux's `SerializedError`, a plain `Error`, or anything else landing in
 * `error: unknown` without fitting `FetchBaseQueryError`. A raw `error.message`
 * is never shown: it can carry backend detail that should not surface. */
const UNKNOWN_ERROR_PRESENTATION: ErrorPresentation = {
  tone: 'danger',
  icon: AlertCircle,
  title: 'Ocurrió un error',
  description: 'No se pudo completar la solicitud. Inténtalo de nuevo.',
  retryable: true,
};

function presentationForHttpStatus(status: number): ErrorPresentation {
  const known = HTTP_STATUS_PRESENTATIONS[status];
  if (known) {
    return known;
  }
  return status >= 500 ? SERVER_ERROR_PRESENTATION : CLIENT_ERROR_PRESENTATION;
}

function resolvePresentation(error: unknown): ErrorPresentation {
  if (isFetchBaseQueryError(error)) {
    const { status } = error;
    if (typeof status === 'number') {
      // 401, 400 and 403 look at the body: in all three, one status hides
      // causes with different actions, and the backend's `code`
      // (`src/modules/auth/auth-error-codes.ts`) is what separates them.
      const code = backendCode(error);
      if (code === 'tenant_selection_required') return TENANT_SELECTION_PRESENTATION;
      if (code === 'tenant_not_a_membership') return TENANT_STALE_PRESENTATION;
      if (code === 'no_active_tenant') return NO_ORGANISATION_PRESENTATION;
      return status === 401 ? unauthorizedPresentation(error) : presentationForHttpStatus(status);
    }
    return RTK_QUERY_REASON_PRESENTATIONS[status];
  }
  return UNKNOWN_ERROR_PRESENTATION;
}

export interface ErrorStateProps {
  /** `FetchBaseQueryError | SerializedError | Error`, not typed that way
   * because none of the three shapes shares structure with the other two. */
  error: unknown;
  /** Domain message; when absent it is derived from the HTTP status. */
  title?: string;
  onRetry?: () => void;
  className?: string;
}

/**
 * Centralises the status → copy mapping (401 → expired session, 403 → no
 * permission, 409 → conflict, 5xx → server error). It replaces the inline
 * mapping every screen repeats today.
 *
 * **`onRetry` is ignored on 401 and 403**, even when the caller passes it:
 * retrying the same query with the same session and the same permissions gives
 * the same status again. A «Reintentar» button that cannot work is worse than
 * none — it makes the user insist instead of doing the one thing that unblocks
 * the case (signing in again, asking for permission). The policy lives here and
 * not in each screen for the same reason the copy does: if each caller decided,
 * some screens would offer the impossible retry and others would not. It is
 * declared in contract §5.1.
 */
export function ErrorState({
  error,
  title,
  onRetry,
  className,
}: ErrorStateProps): React.JSX.Element {
  const presentation = resolvePresentation(error);
  const Icon = presentation.icon;
  const heading = title ?? presentation.title;
  const canRetry = Boolean(onRetry) && presentation.retryable;
  // Only 500s carry an id, and only there is it useful: it is the one error
  // whose detail cannot be shown — a query error's text carries the SQL
  // parameters — so what is offered instead is something the person can quote so
  // somebody else can find the exact line in the log.
  const requestId = backendRequestId(error);

  return (
    // The box is drawn by `StatePanel`, shared with `EmptyState`: both occupy
    // the same slot in `DataTable` and cannot look different. Why that shape was
    // chosen is in `state-panel.tsx`.
    <StatePanel role="alert" className={className}>
      <Icon
        aria-hidden="true"
        className={cn('size-icon-lg', presentation.tone === 'danger' ? 'text-danger' : 'text-warning')}
      />
      <p className="text-body font-semibold text-fg">{heading}</p>
      <p className="text-body-s text-fg-muted">{presentation.description}</p>
      {requestId ? (
        <p className="text-caption text-fg-muted">
          Si necesitas reportarlo, este es el identificador del error:{' '}
          <code className="select-all font-mono">{requestId}</code>
        </p>
      ) : null}
      {canRetry ? (
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw aria-hidden="true" className="size-icon-sm" />
          Reintentar
        </Button>
      ) : null}
    </StatePanel>
  );
}
